import { useRef, useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import ProductCard from '@/components/products/ProductCard';
import AnimatedSection from './AnimatedSection';

const ProductCarousel = ({ products: rawProducts, loading, title, icon: Icon, viewAllLink }) => {
  const products = useMemo(() => Array.isArray(rawProducts) ? rawProducts : [], [rawProducts]);
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 5);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 5);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener('scroll', checkScroll, { passive: true });
    window.addEventListener('resize', checkScroll);
    return () => {
      el.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, [products, loading]);

  const scroll = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = el.querySelector('[data-carousel-item]')?.offsetWidth || 220;
    el.scrollBy({ left: dir === 'left' ? -cardWidth * 2 : cardWidth * 2, behavior: 'smooth' });
  };

  if (!loading && (!products || products.length === 0)) return null;

  const skeletons = [...Array(6)];

  return (
    <AnimatedSection>
      {/* Header */}
      <div className="mb-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {Icon && (
            <div className="p-2 bg-orange-100 rounded-xl">
              <Icon className="w-5 h-5 text-[#FF4500]" />
            </div>
          )}
          <h2 className="font-heading text-xl md:text-2xl font-bold text-slate-900">{title}</h2>
        </div>
        <div className="flex items-center gap-2">
          {/* Desktop arrows */}
          <div className="hidden sm:flex items-center gap-1.5">
            <button
              onClick={() => scroll('left')}
              disabled={!canScrollLeft}
              className={`w-9 h-9 rounded-full border flex items-center justify-center transition-all ${
                canScrollLeft
                  ? 'bg-white border-slate-200 text-slate-600 hover:border-orange-300 hover:text-[#FF4500] shadow-sm'
                  : 'bg-slate-50 border-slate-100 text-slate-300 cursor-default'
              }`}
              data-testid="carousel-arrow-left"
              aria-label="Anterior"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => scroll('right')}
              disabled={!canScrollRight}
              className={`w-9 h-9 rounded-full border flex items-center justify-center transition-all ${
                canScrollRight
                  ? 'bg-white border-slate-200 text-slate-600 hover:border-orange-300 hover:text-[#FF4500] shadow-sm'
                  : 'bg-slate-50 border-slate-100 text-slate-300 cursor-default'
              }`}
              data-testid="carousel-arrow-right"
              aria-label="Proximo"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
          {viewAllLink && (
            <Link
              to={viewAllLink}
              className="flex items-center gap-1 text-[#FF4500] hover:text-orange-700 font-medium text-sm transition-colors ml-2"
            >
              Ver todos <ChevronRight className="w-4 h-4" />
            </Link>
          )}
        </div>
      </div>

      {/* Carousel */}
      <div className="relative -mx-1">
        {/* Fade edges */}
        {canScrollLeft && (
          <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none hidden sm:block" />
        )}
        {canScrollRight && (
          <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none hidden sm:block" />
        )}

        <div
          ref={scrollRef}
          className="flex gap-3 md:gap-4 overflow-x-auto hide-scrollbar scroll-smooth px-1 pb-2"
          data-testid="product-carousel"
        >
          {loading
            ? skeletons.map((_, i) => (
                <div
                  key={i}
                  data-carousel-item
                  className="flex-shrink-0 w-[160px] sm:w-[190px] md:w-[210px] lg:w-[220px]"
                >
                  <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-slate-100">
                    <div className="skeleton aspect-square w-full" />
                    <div className="p-3 space-y-2">
                      <div className="skeleton h-4 w-full" />
                      <div className="skeleton h-4 w-3/4" />
                      <div className="skeleton h-5 w-1/2" />
                      <div className="skeleton h-9 w-full rounded-lg" />
                    </div>
                  </div>
                </div>
              ))
            : products.map((product, idx) => (
                <div
                  key={product.id}
                  data-carousel-item
                  className="flex-shrink-0 w-[160px] sm:w-[190px] md:w-[210px] lg:w-[220px] stagger-item"
                  style={{ animationDelay: `${idx * 60}ms` }}
                >
                  <ProductCard product={product} compact />
                </div>
              ))}
        </div>
      </div>

      {/* Mobile: swipe indicator dots */}
      <div className="flex sm:hidden items-center justify-center gap-1.5 mt-3">
        {products.slice(0, Math.min(products.length, 5)).map((_, i) => (
          <div key={i} className="w-1.5 h-1.5 rounded-full bg-slate-300" />
        ))}
      </div>
    </AnimatedSection>
  );
};

export default ProductCarousel;
